import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adjustOrderPrice } from '../../api/orders';
import { useToast } from '../../context/ToastContext';
import { getFriendlyError } from '../../utils/friendlyError';
import { calculateAdjustedPrice, calculateOrderTotal } from '../../utils/pricingCalculator';
import {
  adjustmentLabel,
  buildOrderBreakdown,
  formatLkr,
  itemBaseUnit,
  itemFinalUnit,
  itemLineTotal,
} from '../../utils/pricingDisplay';

const ItemRow = ({ item, itemIndex, editable, onSave, saving }) => {
  const { t } = useTranslation();
  const base = itemBaseUnit(item);
  const final = itemFinalUnit(item);
  const hasAdj = item.adjustmentType && item.adjustmentType !== 'NONE';
  const [open, setOpen] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState(item.adjustmentType || 'NONE');
  const [adjustmentMode, setAdjustmentMode] = useState(item.adjustmentMode || 'FLAT');
  const [adjustmentValue, setAdjustmentValue] = useState(String(item.adjustmentValue ?? ''));
  const [adjustmentReason, setAdjustmentReason] = useState(item.adjustmentReason || '');

  useEffect(() => {
    setAdjustmentType(item.adjustmentType || 'NONE');
    setAdjustmentMode(item.adjustmentMode || 'FLAT');
    setAdjustmentValue(item.adjustmentValue != null ? String(item.adjustmentValue) : '');
    setAdjustmentReason(item.adjustmentReason || '');
  }, [item.adjustmentType, item.adjustmentMode, item.adjustmentValue, item.adjustmentReason]);

  const previewUnit = (() => {
    try {
      return calculateAdjustedPrice(
        base,
        adjustmentType,
        Number(adjustmentValue || 0),
        adjustmentType === 'MANUAL_OVERRIDE' ? 'FLAT' : adjustmentMode
      );
    } catch {
      return base;
    }
  })();

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-3">
      <div className="flex gap-3">
        {item.productId?.images?.[0]?.url && (
          <img src={item.productId.images[0].url} alt="" className="h-14 w-14 rounded object-cover" />
        )}
        <div className="flex-1 text-sm">
          <p className="font-medium">{item.productName}</p>
          <p className="text-zinc-600 dark:text-zinc-500">
            × {item.quantity}
            {hasAdj && base > final && (
              <span className="ml-1 line-through">{formatLkr(base * item.quantity)}</span>
            )}
            {' · '}
            <span className={hasAdj ? 'text-glow-300' : ''}>{formatLkr(itemLineTotal(item))}</span>
          </p>
          {hasAdj && item.adjustmentReason && (
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-500">{item.adjustmentReason}</p>
          )}
          {editable && (
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="mt-1 text-xs text-glow-400 hover:underline"
            >
              {open ? t('pricing.hideAdjustment') : t('pricing.adjustItemPrice')}
            </button>
          )}
        </div>
      </div>

      {editable && open && (
        <div className="mt-3 space-y-2 border-t border-zinc-200 dark:border-zinc-800 pt-3">
          <p className="text-xs text-zinc-600 dark:text-zinc-500">{t('pricing.baseUnit', { amount: formatLkr(base) })}</p>
          <select
            value={adjustmentType}
            onChange={(e) => setAdjustmentType(e.target.value)}
            className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
          >
            <option value="NONE">{t('pricing.noChange')}</option>
            <option value="DISCOUNT">{t('pricing.discount')}</option>
            <option value="SURCHARGE">{t('pricing.surcharge')}</option>
            <option value="MANUAL_OVERRIDE">{t('pricing.manualOverride')}</option>
          </select>
          {adjustmentType !== 'NONE' && adjustmentType !== 'MANUAL_OVERRIDE' && (
            <select
              value={adjustmentMode}
              onChange={(e) => setAdjustmentMode(e.target.value)}
              className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
            >
              <option value="FLAT">{t('pricing.flatLkr')}</option>
              <option value="PERCENTAGE">{t('pricing.percentageSymbol')}</option>
            </select>
          )}
          {adjustmentType !== 'NONE' && (
            <input
              type="number"
              min="0"
              step="0.01"
              value={adjustmentValue}
              onChange={(e) => setAdjustmentValue(e.target.value)}
              className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
            />
          )}
          <textarea
            value={adjustmentReason}
            onChange={(e) => setAdjustmentReason(e.target.value.slice(0, 500))}
            placeholder={t('pricing.reasonOptionalShort')}
            rows={2}
            className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
          />
          <p className="text-xs">{t('pricing.previewAmount', { amount: formatLkr(previewUnit * item.quantity) })}</p>
          <button
            type="button"
            disabled={saving || (adjustmentType !== 'NONE' && adjustmentValue === '')}
            onClick={() =>
              onSave({
                itemIndex,
                adjustmentType,
                adjustmentValue: Number(adjustmentValue || 0),
                adjustmentMode: adjustmentType === 'MANUAL_OVERRIDE' ? 'FLAT' : adjustmentMode,
                adjustmentReason: adjustmentReason.trim(),
              })
            }
            className="w-full rounded-lg border border-glow-600 py-1.5 text-xs text-glow-300 disabled:opacity-50"
          >
            {t('pricing.saveItemPrice')}
          </button>
        </div>
      )}
    </div>
  );
};

export const OrderPricingBreakdown = ({ order }) => {
  const { t } = useTranslation();
  const { items, itemAdjustmentsTotal, subtotalAmount, orderAdjAmount, totalAmount } = buildOrderBreakdown(order);
  const oa = order.orderAdjustment || {};

  return (
    <div className="space-y-2 rounded-lg border border-zinc-200 dark:border-zinc-800 p-3 text-sm">
      {items.map((row, i) => (
        <div key={i} className="space-y-1">
          <div className="flex justify-between gap-2 text-zinc-600 dark:text-zinc-400">
            <span className="truncate">{row.item.productName} × {row.item.quantity}</span>
            <span>{formatLkr(row.lineTotal)}</span>
          </div>

          {row.baseLine !== row.lineTotal && (
            <div className="text-xs text-zinc-500">
              <span className="line-through">{formatLkr(row.baseLine)}</span>
              <span className="ml-2 text-zinc-400">
                {formatLkr(row.lineTotal)}
              </span>
            </div>
          )}

          {row.item.adjustmentType && row.item.adjustmentType !== 'NONE' && (
            <div className="text-xs text-emerald-400">
              {adjustmentLabel(row.item.adjustmentType, row.item.adjustmentValue, row.item.adjustmentMode)}
            </div>
          )}

          {row.item.adjustmentReason && (
            <div className="text-xs text-zinc-600 dark:text-zinc-500">{row.item.adjustmentReason}</div>
          )}
        </div>
      ))}
      <div className="border-t border-zinc-200 dark:border-zinc-800 pt-2 space-y-1">
        <div className="flex justify-between">
          <span>{t('common.subtotal')}</span>
          <span>{formatLkr(subtotalAmount)}</span>
        </div>
        {itemAdjustmentsTotal !== 0 && (
          <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
            <span>{t('pricing.itemAdjustments')}</span>
            <span>{itemAdjustmentsTotal > 0 ? '+' : ''}{formatLkr(itemAdjustmentsTotal)}</span>
          </div>
        )}
        {oa.adjustmentType && oa.adjustmentType !== 'NONE' && (
          <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
            <span>{adjustmentLabel(oa.adjustmentType, oa.adjustmentValue, oa.adjustmentMode)}</span>
            <span>{orderAdjAmount > 0 ? '+' : ''}{formatLkr(orderAdjAmount)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold text-glow-300">
          <span>{t('common.total')}</span>
          <span>{formatLkr(totalAmount)}</span>
        </div>
      </div>
      {oa.adjustmentReason && (
        <p className="text-xs text-zinc-600 dark:text-zinc-500">{t('pricing.orderNote', { reason: oa.adjustmentReason })}</p>
      )}
    </div>
  );
};

const OrderPricingManager = ({ order, onUpdated }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const oa = order.orderAdjustment || {};
  const [orderAdjType, setOrderAdjType] = useState(oa.adjustmentType || 'NONE');
  const [orderAdjMode, setOrderAdjMode] = useState(oa.adjustmentMode || 'FLAT');
  const [orderAdjValue, setOrderAdjValue] = useState(String(oa.adjustmentValue ?? ''));
  const [orderAdjReason, setOrderAdjReason] = useState(oa.adjustmentReason || '');

  useEffect(() => {
    setOrderAdjType(oa.adjustmentType || 'NONE');
    setOrderAdjMode(oa.adjustmentMode || 'FLAT');
    setOrderAdjValue(oa.adjustmentValue != null ? String(oa.adjustmentValue) : '');
    setOrderAdjReason(oa.adjustmentReason || '');
  }, [order._id, oa.adjustmentType, oa.adjustmentMode, oa.adjustmentValue, oa.adjustmentReason]);

  const editable = ['PENDING', 'CONFIRMED'].includes(order.status);

  const adjustMut = useMutation({
    mutationFn: (body) => adjustOrderPrice(order._id, body),
    onSuccess: () => {
      showToast(t('toast.pricingUpdated'), 'success');
      onUpdated();
    },
    onError: (err) => showToast(getFriendlyError(err, t('pricing.updatePricingFailed')), 'error'),
  });

  const previewItems = order.items.map((item) => ({
    ...item,
    finalUnitPrice: itemFinalUnit(item),
  }));
  const preview = calculateOrderTotal(previewItems, {
    adjustmentType: orderAdjType,
    adjustmentValue: Number(orderAdjValue || 0),
    adjustmentMode: orderAdjMode,
  });

  if (!editable) {
    return <OrderPricingBreakdown order={order} />;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm font-medium">{t('orders.drawer.items')}</p>
      {order.items.map((item, i) => (
        <ItemRow
          key={i}
          item={item}
          itemIndex={i}
          editable
          saving={adjustMut.isPending}
          onSave={(itemAdjustment) => adjustMut.mutate({ itemAdjustments: [itemAdjustment] })}
        />
      ))}

      <div className="space-y-2 rounded-lg border border-zinc-200 dark:border-zinc-800 p-3">
        <p className="text-sm font-medium">{t('pricing.orderLevelAdjustment')}</p>
        <select
          value={orderAdjType}
          onChange={(e) => setOrderAdjType(e.target.value)}
          className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
        >
          <option value="NONE">{t('pricing.none')}</option>
          <option value="DISCOUNT">{t('pricing.discount')}</option>
          <option value="SURCHARGE">{t('pricing.surcharge')}</option>
        </select>
        {orderAdjType !== 'NONE' && (
          <>
            <select
              value={orderAdjMode}
              onChange={(e) => setOrderAdjMode(e.target.value)}
              className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
            >
              <option value="FLAT">{t('pricing.flatLkr')}</option>
              <option value="PERCENTAGE">{t('pricing.percentageSymbol')}</option>
            </select>
            <input
              type="number"
              min="0"
              step="0.01"
              value={orderAdjValue}
              onChange={(e) => setOrderAdjValue(e.target.value)}
              className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
            />
            <textarea
              value={orderAdjReason}
              onChange={(e) => setOrderAdjReason(e.target.value.slice(0, 500))}
              placeholder={t('pricing.reasonOptionalShort')}
              rows={2}
              className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm"
            />
          </>
        )}
        <button
          type="button"
          disabled={adjustMut.isPending || (orderAdjType !== 'NONE' && orderAdjValue === '')}
          onClick={() =>
            adjustMut.mutate({
              orderAdjustment: {
                adjustmentType: orderAdjType,
                adjustmentValue: Number(orderAdjValue || 0),
                adjustmentMode: orderAdjMode,
                adjustmentReason: orderAdjReason.trim(),
              },
            })
          }
          className="w-full rounded-lg bg-glow-600 py-2 text-sm text-white disabled:opacity-50"
        >
          {t('pricing.applyOrderAdjustment')}
        </button>
      </div>

      <OrderPricingBreakdown order={{
        ...order,
        subtotalAmount: preview.subtotalAmount,
        totalAmount: preview.totalAmount,
        orderAdjustment: {
          adjustmentType: orderAdjType,
          adjustmentValue: Number(orderAdjValue || 0),
          adjustmentMode: orderAdjMode,
          adjustmentReason: orderAdjReason,
        },
      }} />
    </div>
  );
};

export default OrderPricingManager;
